// Paramètres - Configuration du compte
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User, Mail, Phone, MapPin, Bell, Lock, Shield, Eye, EyeOff,
  Save, ChevronRight, Trash2, LogOut, CreditCard, X, AlertCircle, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function SettingsSection({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function ToggleSetting({ label, description, checked, onChange }) {
  return (
    <label className="flex items-center justify-between py-3 cursor-pointer">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        {description && <p className="text-sm text-gray-500">{description}</p>}
      </div>
      <div className={`relative w-12 h-6 rounded-full transition-colors ${checked ? 'bg-orange-500' : 'bg-gray-300'}`}>
        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-0.5'}`} />
        <input 
          type="checkbox" 
          checked={checked} 
          onChange={(e) => onChange(e.target.checked)} 
          className="sr-only" 
        />
      </div>
    </label>
  );
}

// Modal changement de mot de passe
function ChangePasswordModal({ isOpen, onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('ecosystem_token');
      const response = await fetch(`${API_URL}/api/ecosystem/settings/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.detail || 'Erreur lors du changement de mot de passe');
        return;
      }
      
      toast.success('Mot de passe modifié avec succès');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Changer le mot de passe</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe actuel</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
              <button 
                type="button" 
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
                minLength={8}
              />
              <button 
                type="button" 
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le nouveau mot de passe</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showNew ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
              {confirmPassword && newPassword === confirmPassword && (
                <Check size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {loading ? 'Modification...' : 'Modifier'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Modal 2FA
function TwoFactorModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ecosystem_token');
      const response = await fetch(`${API_URL}/api/ecosystem/settings/enable-2fa`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      toast.success(data.message);
      onClose();
    } catch (err) {
      toast.error('Erreur lors de l\'activation');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Authentification à deux facteurs</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield size={24} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-1">Sécurise ton compte</h4>
              <p className="text-sm text-gray-600">
                L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire. 
                Tu recevras un code par SMS à chaque connexion.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-800">
              <strong>Bientôt disponible :</strong> Cette fonctionnalité est en cours de déploiement. 
              Tu seras notifié dès qu'elle sera active.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
            <Button
              onClick={handleEnable}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? 'Activation...' : 'Me notifier quand disponible'}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Modal Moyens de paiement
function PaymentMethodsModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Moyens de paiement</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CreditCard size={32} className="text-gray-400" />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Aucune carte enregistrée</h4>
            <p className="text-sm text-gray-500 mb-6">
              Ajoute un moyen de paiement pour effectuer des achats sur la plateforme.
            </p>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              Ajouter une carte
            </Button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              <strong>Paiements sécurisés :</strong> Tes données sont protégées par Stripe, 
              leader mondial du paiement en ligne.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function ParametresPage() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Modals
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    ville: '',
    codePostal: '',
  });
  
  const [originalProfile, setOriginalProfile] = useState({});

  const [notifications, setNotifications] = useState({
    newMessage: true,
    newOffer: true,
    priceAlert: true,
    newsletter: false,
    sms: false,
  });

  const [privacy, setPrivacy] = useState({
    showPhone: false,
    showEmail: false,
    publicProfile: true,
  });

  // Charger les données depuis le backend
  useEffect(() => {
    const loadSettings = async () => {
      const token = localStorage.getItem('ecosystem_token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/ecosystem/settings/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const userData = data.user || {};
          const settings = data.settings || {};
          
          const loadedProfile = {
            name: userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || '',
            email: userData.email || '',
            phone: userData.phone || '',
            ville: userData.city || '',
            codePostal: userData.postal_code || '',
          };
          
          setProfile(loadedProfile);
          setOriginalProfile(loadedProfile);
          
          setNotifications({
            newMessage: settings.notifications_email !== false,
            newOffer: settings.notifications_email !== false,
            priceAlert: settings.notifications_email !== false,
            newsletter: false,
            sms: settings.notifications_sms || false,
          });
          
          setPrivacy({
            showPhone: settings.privacy_show_phone || false,
            showEmail: settings.privacy_show_email || false,
            publicProfile: settings.privacy_public_profile !== false,
          });
        }
      } catch (error) {
        console.error('Erreur chargement paramètres:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);
  
  // Détecter les changements
  useEffect(() => {
    const changed = JSON.stringify(profile) !== JSON.stringify(originalProfile);
    setHasChanges(changed);
  }, [profile, originalProfile]);

  const handleSave = async () => {
    const token = localStorage.getItem('ecosystem_token');
    if (!token) {
      toast.error('Tu dois être connecté pour modifier tes paramètres');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ecosystem/settings/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: profile.name,
          phone: profile.phone,
          ville: profile.ville,
          code_postal: profile.codePostal,
          notifications_email: notifications.newMessage,
          notifications_sms: notifications.sms,
          privacy_show_phone: privacy.showPhone,
          privacy_show_email: privacy.showEmail,
          privacy_public_profile: privacy.publicProfile,
        })
      });

      if (response.ok) {
        toast.success('Paramètres enregistrés');
        setOriginalProfile(profile);
        setHasChanges(false);
        
        // Mettre à jour le localStorage aussi
        const ecosystemUser = localStorage.getItem('ecosystem_user');
        if (ecosystemUser) {
          try {
            const parsed = JSON.parse(ecosystemUser);
            parsed.name = profile.name;
            parsed.phone = profile.phone;
            parsed.city = profile.ville;
            parsed.postal_code = profile.codePostal;
            localStorage.setItem('ecosystem_user', JSON.stringify(parsed));
          } catch(e) {}
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = () => {
    if (confirm('Es-tu sûr de vouloir supprimer ton compte ? Cette action est irréversible.')) {
      toast.error('Fonctionnalité en cours de développement');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ecosystem_token');
    localStorage.removeItem('ecosystem_user');
    localStorage.removeItem('ecosystem_profile');
    localStorage.removeItem('buyer_token');
    localStorage.removeItem('buyer_user');
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    toast.success('Déconnexion réussie');
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Modals */}
      <ChangePasswordModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} />
      <TwoFactorModal isOpen={show2FAModal} onClose={() => setShow2FAModal(false)} />
      <PaymentMethodsModal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} />

      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Paramètres</h1>
        <p className="text-gray-500">Gère ton compte et tes préférences</p>
      </div>

      {/* Alerte changements non sauvegardés */}
      {hasChanges && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle size={20} className="text-orange-500" />
            <p className="text-sm text-orange-800 font-medium">Tu as des modifications non enregistrées</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      )}

      {/* Informations personnelles */}
      <SettingsSection title="Informations personnelles">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                data-testid="settings-name-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={profile.email}
                disabled
                className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                data-testid="settings-email-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <div className="relative">
              <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                placeholder="06 12 34 56 78"
                className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                data-testid="settings-phone-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
              <input
                type="text"
                value={profile.ville}
                onChange={(e) => setProfile(p => ({ ...p, ville: e.target.value }))}
                placeholder="Lyon"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                data-testid="settings-city-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
              <input
                type="text"
                value={profile.codePostal}
                onChange={(e) => setProfile(p => ({ ...p, codePostal: e.target.value }))}
                placeholder="69001"
                maxLength={5}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                data-testid="settings-postal-input"
              />
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Notifications */}
      <SettingsSection title="Notifications">
        <div className="divide-y divide-gray-100">
          <ToggleSetting
            label="Nouveaux messages"
            description="Recevoir une notification pour chaque nouveau message"
            checked={notifications.newMessage}
            onChange={(v) => setNotifications(n => ({ ...n, newMessage: v }))}
          />
          <ToggleSetting
            label="Nouvelles offres"
            description="Quand quelqu'un fait une offre sur une de tes annonces"
            checked={notifications.newOffer}
            onChange={(v) => setNotifications(n => ({ ...n, newOffer: v }))}
          />
          <ToggleSetting
            label="Alertes prix"
            description="Quand un article de tes favoris baisse de prix"
            checked={notifications.priceAlert}
            onChange={(v) => setNotifications(n => ({ ...n, priceAlert: v }))}
          />
          <ToggleSetting
            label="Newsletter"
            description="Recevoir nos actualités et conseils par email"
            checked={notifications.newsletter}
            onChange={(v) => setNotifications(n => ({ ...n, newsletter: v }))}
          />
          <ToggleSetting
            label="Notifications SMS"
            description="Recevoir certaines alertes par SMS"
            checked={notifications.sms}
            onChange={(v) => setNotifications(n => ({ ...n, sms: v }))}
          />
        </div>
      </SettingsSection>

      {/* Confidentialité */}
      <SettingsSection title="Confidentialité">
        <div className="divide-y divide-gray-100">
          <ToggleSetting
            label="Afficher mon téléphone"
            description="Les acheteurs pourront voir ton numéro"
            checked={privacy.showPhone}
            onChange={(v) => setPrivacy(p => ({ ...p, showPhone: v }))}
          />
          <ToggleSetting
            label="Afficher mon email"
            description="Les acheteurs pourront voir ton email"
            checked={privacy.showEmail}
            onChange={(v) => setPrivacy(p => ({ ...p, showEmail: v }))}
          />
          <ToggleSetting
            label="Profil public"
            description="Ton profil est visible par tous"
            checked={privacy.publicProfile}
            onChange={(v) => setPrivacy(p => ({ ...p, publicProfile: v }))}
          />
        </div>
      </SettingsSection>

      {/* Sécurité */}
      <SettingsSection title="Sécurité">
        <div className="space-y-4">
          <button 
            onClick={() => setShowPasswordModal(true)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
            data-testid="change-password-btn"
          >
            <div className="flex items-center gap-3">
              <Lock size={20} className="text-gray-500" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Changer le mot de passe</p>
                <p className="text-sm text-gray-500">Modifie ton mot de passe actuel</p>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button 
            onClick={() => setShow2FAModal(true)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
            data-testid="2fa-btn"
          >
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-gray-500" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Authentification à deux facteurs</p>
                <p className="text-sm text-gray-500">Non activée</p>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>

          <button 
            onClick={() => setShowPaymentModal(true)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
            data-testid="payment-methods-btn"
          >
            <div className="flex items-center gap-3">
              <CreditCard size={20} className="text-gray-500" />
              <div className="text-left">
                <p className="font-medium text-gray-900">Moyens de paiement</p>
                <p className="text-sm text-gray-500">Gérer tes cartes</p>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </button>
        </div>
      </SettingsSection>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <button 
          onClick={handleDeleteAccount}
          className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium"
        >
          <Trash2 size={18} />
          Supprimer mon compte
        </button>

        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="bg-orange-500 hover:bg-orange-600 text-white px-8 disabled:opacity-50"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save size={18} className="mr-2" />
              Enregistrer
            </>
          )}
        </Button>
      </div>

      {/* Déconnexion */}
      <div className="pt-6 border-t border-gray-200">
        <button 
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 p-4 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 font-medium transition-colors"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
